import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useRouteMatch, useHistory } from 'react-router-dom';
import { useDisclosure } from 'further-ui/hooks';
import { round } from 'lodash';
import PageContainer from 'components/PageContainer';
import ExitDetailsForm from './ExitDetailsForm';
import ReviewShareholdings from './ReviewShareholdings';
import ReviewShareConsiderations from './ReviewShareConsiderations';
import ButtonsAndErrorModals from './ButtonsAndErrorModals';
import ConsiderationHistory from './ExitDetailsForm/ConsiderationHistory';
import FullPageSuccess from 'components/FullPageSuccess';
import AlertDialog from 'components/AlertDialog';
import BasicSelect from 'further-ui/components/BasicSelect';
import {
  EXITS,
  EXITS_EDIT,
  EXITS_CORRECT,
  createExitStatementAddRoute,
} from 'adminConstants/routes';
import downloadFile from 'utils/downloadFile';
import base64ToBlob from 'utils/base64ToBlob';
import { BottomTextContent } from './BottomTextContent';
import { format } from 'date-fns';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import Heading from 'components/Heading';
import api, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  ConsiderationType,
  ShareConsiderationAllocationMethod,
  ConsiderationHistoryItemType,
} from 'further-types/exit';
import { styled } from '@mui/material/styles';
import type { ExitDetailsFormValues } from './ExitDetailsForm';
import type { ShareConsideration } from './types';
import type {
  ConsiderationHistoryItemData,
  PreviousExitDetails,
} from './ExitDetailsForm/types';

const StyledHeading = styled(Heading)(() => ({
  marginTop: '2rem',
}));

const CorrectionModalContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  alignItems: 'stretch',
}));

const CorrectionBehaviorDescription = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: 0,
}));

const CorrectionBehaviorContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
}));

const CorrectionBehaviorSelectWrapper = styled('div')(() => ({
  flex: 1,
  justifyContent: 'center',
  '& > div': {
    minWidth: '360px',
    textAlign: 'left',
  },
}));

type Shareholding = {
  shareholdingId: string;
  sharesAllocatedForSale: number;
  totalSaleValue: number;
  taxableValue: number;
  performanceFee: number;
  sharePrice: number;
  taxableValueSharePrice: number;
  accruedFeesCharged?: number;
  additionalGrossReceivedValue?: number;
  grossTaxableValue?: number;
  previousGrossReceivedValue?: number;
};

const CreateExit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const isBeingEdited = useRouteMatch(EXITS_EDIT)?.isExact;
  const isBeingCorrected = useRouteMatch(EXITS_CORRECT)?.isExact;

  const [previousExitDetails, setPreviousExitDetails] = useState<
    PreviousExitDetails | undefined
  >(undefined);
  const [exitDetails, setExitDetails] = useState<
    ExitDetailsFormValues | undefined
  >(undefined);
  const [exitDetailsStepCompleted, setExitDetailsStepCompleted] =
    useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shareholdings, setShareholdings] = useState<Array<Shareholding>>([]);
  const [shareConsiderations, setShareConsiderations] = useState<
    Array<ShareConsideration>
  >([]);
  const [exitId, setExitId] = useState(id);
  const [amendmentId, setAmendmentId] = useState<string | undefined>(undefined);
  const [disableTable, setDisableTable] = useState<boolean>(false);
  const [isUploadComplete, setIsUploadComplete] = useState<boolean>(false);
  const [skipPrepopulation, setSkipPrepopulation] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Correction behavior modal state
  const correctionBehaviorDisclosure = useDisclosure();
  const [pendingExitDetails, setPendingExitDetails] = useState<
    ExitDetailsFormValues | undefined
  >(undefined);
  const [maintainOriginalAllocations, setMaintainOriginalAllocations] =
    useState<boolean>(true);

  const handleMutation = useHandleTrpcMutation();
  const exit = api.exit.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
    },
  );

  // Determine if we're correcting an amendment (has existing amendments) or original exit (no amendments)
  // This affects which fields are editable in the form
  const isCorrectingAmendment = isBeingCorrected && exit.data?.hasAmendments;

  const create = api.exit.create.useMutation(
    handleMutation({ refetchType: 'none' }),
  );

  const createShareConsiderationExit =
    api.exit.createShareConsiderationExit.useMutation(
      handleMutation({ refetchType: 'none' }),
    );

  const edit = api.exit.edit.useMutation(
    handleMutation({ refetchType: 'none' }),
  );

  const editShareConsiderationExit =
    api.exit.editShareConsiderationExit.useMutation(
      handleMutation({ refetchType: 'none' }),
    );

  const correctExit = api.exit.correctExit.useMutation(
    handleMutation({ refetchType: 'none', successRedirect: EXITS }),
  );

  const correctShareConsiderationExit =
    api.exit.correctShareConsiderationExit.useMutation(
      handleMutation({ refetchType: 'none', successRedirect: EXITS }),
    );

  const considerationHistoryEnabled =
    !!exitId &&
    !!exit.data?.firmId &&
    exitDetailsStepCompleted &&
    (isBeingEdited || isBeingCorrected);

  const considerationHistory = api.exit.getConsiderationHistory.useQuery(
    {
      exitId: exitId ?? '',
      firmId: exit.data?.firmId ?? '',
    },
    {
      enabled: considerationHistoryEnabled,
    },
  );

  const downloadExit = api.exit.download.useMutation(
    useHandleTrpcMutation<ApiResponses['exit']['download']>()({
      onSuccess: (data) => {
        const blob = base64ToBlob(data.file, data.mimeType);
        downloadFile(blob, data.filename || `Exit-${Date.now()}.xlsx`);
      },
    }),
  );

  const handleDownload = async () => {
    if (!exitId) return;
    setIsDownloading(true);
    downloadExit.mutate(
      { id: exitId },
      {
        onSettled: () => {
          setIsDownloading(false);
        },
      },
    );
  };

  useEffect(() => {
    // Prepopulate form data for both edit and correction modes
    if ((isBeingEdited || isBeingCorrected) && id && exit.data) {
      const exitData = exit.data as typeof exit.data & {
        shareConsiderationExit?: boolean;
        shareConsiderationCompanyId?: string;
        shareConsiderationShareClassId?: string;
        totalConsiderationShares?: number;
        shareConsiderationValuePerShare?: number;
        shareConsiderationValueTotal?: number;
        shareConsiderationEisEligible?: boolean;
        originalExitDate?: string;
        latestAmendmentId?: string;
        chargeAccruedFees?: boolean;
      };
      const {
        firmId,
        firmName,
        date,
        originalExitDate,
        latestAmendmentId,
        companyIds,
        companies,
        noSharesExited,
        sharePrice,
        taxReliefElegible,
        taxReliefElegibleAllocationIds,
        taxableValueSharePrice,
        shareConsiderationExit,
        shareConsiderationCompanyId,
        shareConsiderationShareClassId,
        totalConsiderationShares,
        shareConsiderationValuePerShare,
        shareConsiderationValueTotal,
        shareConsiderationEisEligible,
        originalTaxReliefElegible,
        originalTaxableValueSharePrice,
        chargeAccruedFees: exitChargeAccruedFees,
      } = exitData || {};

      // When correcting: only send exitAmendmentId if this exit has amendments (otherwise we're correcting the original exit)
      if (isBeingCorrected) {
        setAmendmentId(latestAmendmentId ?? undefined);
      }

      const sharesCount = Number(noSharesExited) || 0;
      const taxablePerShare = Number(taxableValueSharePrice) || 0;
      const receivedPerShare = Number(sharePrice) || 0;

      const taxableValueTotal =
        sharesCount > 0 && taxablePerShare >= 0
          ? round(taxablePerShare * sharesCount, 2).toString()
          : '0';
      const receivedValueTotal =
        sharesCount > 0 && receivedPerShare >= 0
          ? round(receivedPerShare * sharesCount, 2).toString()
          : '0';

      const details: ExitDetailsFormValues = {
        firmId: firmId ?? '',
        exitDate: new Date(date),
        firmName: firmName ?? '',
        selectedCompany: {
          _id: companies?.[0]?._id ?? '',
          tradingName: companies?.[0]?.tradingName ?? '',
        },
        sharesToBeSold: sharesCount,
        sharePrice: sharePrice?.toString() ?? '0',
        taxableValueSharePrice: taxableValueSharePrice?.toString() ?? '0',
        taxableValueTotal,
        receivedValueTotal,
        isReceivedValueSharePriceEdited: false,
        chargeAccruedFees: exitChargeAccruedFees ?? false,
        taxReliefElegible: taxReliefElegible ?? undefined,
        selectedShareClasses: companyIds || [],
        companyIds: companyIds || [],
        selectedAllocations: exit.data?.shareAllocationUploadIds || [],
        shareAllocationUploadIds: exit.data?.shareAllocationUploadIds || [],
        taxReliefElegibleAllocationIds: taxReliefElegibleAllocationIds || [],
        originalTaxReliefElegible: originalTaxReliefElegible ?? undefined,
        originalTaxableValueSharePrice:
          originalTaxableValueSharePrice?.toString() ?? '0',
        considerationType: shareConsiderationExit
          ? ConsiderationType.Share
          : ConsiderationType.Cash,
        shareConsiderationCompanyId: shareConsiderationCompanyId ?? undefined,
        shareConsiderationShareClassId,
        totalConsiderationShares: totalConsiderationShares?.toString() ?? '0',
        shareConsiderationValuePerShare:
          shareConsiderationValuePerShare?.toString() ?? '0',
        shareConsiderationValueTotal:
          shareConsiderationValueTotal?.toString() ?? '0',
        shareConsiderationEisEligible:
          shareConsiderationEisEligible ?? undefined,
        shareConsiderationAllocationMethod: isBeingCorrected
          ? ShareConsiderationAllocationMethod.Automatic
          : undefined,
        permitPartialShareAllocation: false,
      };

      setExitDetails(details);
      const allocationIds = exit.data?.shareAllocationUploadIds ?? [];
      setPreviousExitDetails({
        ...details,
        shareAllocationUploadIds: Array.isArray(allocationIds)
          ? allocationIds
          : [],
        originalExitDate: originalExitDate
          ? new Date(originalExitDate)
          : undefined,
      });
    }
  }, [id, isBeingEdited, isBeingCorrected, exit.data]);

  // Prepopulate share considerations or shareholdings for corrections
  useEffect(() => {
    if (
      isBeingCorrected &&
      !skipPrepopulation &&
      considerationHistory.data &&
      considerationHistory.data.length > 0
    ) {
      // Get the last (latest) consideration history entry
      const lastHistoryItem =
        considerationHistory.data[considerationHistory.data.length - 1];

      if (!lastHistoryItem || !Array.isArray(lastHistoryItem.data)) {
        return;
      }

      // Handle share consideration exits
      if (
        exit.data?.shareConsiderationExit &&
        lastHistoryItem.type ===
          ConsiderationHistoryItemType.ShareConsiderationExit
      ) {
        // Transform the data to match ShareConsideration type expected by ReviewShareConsiderations
        const transformedData: Array<ShareConsideration> = (
          lastHistoryItem.data as Array<ConsiderationHistoryItemData>
        ).map((item) => ({
          shareholdingId: item.shareholdingId,
          shareClass: item.shareClass ?? '',
          noOfShares: item.noOfShares ?? 0,
          sharesAllocatedForSale: item.sharesAllocatedForSale ?? 0,
          sharePrice: item.sharePrice ?? 0,
          accruedFeesCharged: item.accruedFeesCharged ?? 0,
          performanceFee: item.performanceFee ?? 0,
          newSharesAllocated: item.newSharesAllocated ?? 0,
          allocationDate: item.allocationDate,
          investorName: item.investorName,
          tranche: item.tranche,
          sourceInvestmentId: item.sourceInvestmentId,
          transferSourceInvestor: item.transferSourceInvestor,
          transferDate: item.transferDate,
          investor: item.investor
            ? { fullName: item.investor.fullName }
            : undefined,
        }));

        setShareConsiderations(transformedData);
      }
      // Handle cash consideration exits
      else if (
        !exit.data?.shareConsiderationExit &&
        lastHistoryItem.type === ConsiderationHistoryItemType.CashExit
      ) {
        // Transform the data to match Shareholding type expected by ReviewShareholdings
        const transformedData = (
          lastHistoryItem.data as Array<ConsiderationHistoryItemData>
        ).map((item) => ({
          // Core calculation fields
          shareholdingId: item.shareholdingId,
          sharesAllocatedForSale: item.sharesAllocatedForSale ?? 0,
          totalSaleValue: item.totalSaleValue ?? 0,
          taxableValue: item.taxableValue ?? 0,
          performanceFee: item.performanceFee ?? 0,
          sharePrice: item.sharePrice ?? 0,
          taxableValueSharePrice: item.taxableValueSharePrice ?? 0,
          accruedFeesCharged: item.accruedFeesCharged,
          additionalGrossReceivedValue: item.additionalGrossReceivedValue,
          grossTaxableValue: item.grossTaxableValue,
          // Display fields for the table
          investorName: item.investorName,
          tranche: item.tranche,
          allocationDate: item.allocationDate,
          shareClass: item.shareClass,
          noOfShares: item.noOfShares,
          sourceInvestmentId: item.sourceInvestmentId,
          transferSourceInvestor: item.transferSourceInvestor,
          transferDate: item.transferDate,
          company: item.company,
          investor: item.investor,
        })) as Array<Shareholding>;

        setShareholdings(transformedData);
      }
    }
  }, [
    isBeingCorrected,
    skipPrepopulation,
    considerationHistory.data,
    exit.data?.shareConsiderationExit,
    id,
  ]);

  useEffect(() => {
    // On successful correction, navigate back to exits list
    if (correctExit.data || correctShareConsiderationExit.data) {
      history.push(EXITS);
      return;
    }

    if (
      create.data ||
      createShareConsiderationExit.data ||
      edit.data ||
      editShareConsiderationExit.data
    ) {
      setAmendmentId(
        edit?.data?.exitAmendmentId ??
          editShareConsiderationExit?.data?.exitAmendmentId,
      );
      setIsSuccess(true);
    }

    if (create.data?._id) {
      setExitId(create.data._id);
    }

    if (createShareConsiderationExit.data?._id) {
      setExitId(createShareConsiderationExit.data._id);
    }
  }, [
    create.data,
    createShareConsiderationExit.data,
    edit.data,
    editShareConsiderationExit.data,
    correctExit.data,
    correctShareConsiderationExit.data,
    history,
  ]);

  const removeEmptyShareholdingRows = (sh) => {
    if (
      !sh.sharesAllocatedForSale &&
      !sh.accruedFeesCharged &&
      !sh.performanceFee &&
      !sh.totalSaleValue
    )
      return false;
    return true;
  };

  const onSubmit = async () => {
    if (!exitDetails) return;
    if (!exitDetails.taxReliefElegible) return;

    // Validate that companyIds is not empty in create mode (in edit/correction mode, classes are locked/read-only)
    if (
      !isBeingEdited &&
      !isBeingCorrected &&
      (!exitDetails.selectedShareClasses ||
        exitDetails.selectedShareClasses.length === 0)
    ) {
      return;
    }

    const commonMutationInput = {
      firmId: exitDetails.firmId ?? '',
      sharePrice: Number(exitDetails.sharePrice),
      taxableValueSharePrice: Number(exitDetails.taxableValueSharePrice),
      taxReliefElegible: exitDetails.taxReliefElegible ?? undefined,
      taxReliefElegibleAllocationIds:
        exitDetails.taxReliefElegibleAllocationIds,
    };

    // Both edit and correction modes
    if (isBeingEdited || isBeingCorrected) {
      if (!exitId) return;

      // Check if this is a share consideration exit
      const isShareConsideration =
        exitDetails.considerationType === ConsiderationType.Share;

      if (isShareConsideration) {
        // Use correct endpoint for corrections, edit for normal edits
        const mutationToUse = isBeingCorrected
          ? correctShareConsiderationExit
          : editShareConsiderationExit;

        mutationToUse.mutate({
          ...commonMutationInput,
          shareConsiderationsToBeExited: shareConsiderations
            .filter(
              (sc) =>
                (sc.sharesAllocatedForSale ?? 0) > 0 ||
                (sc.newSharesAllocated ?? 0) > 0,
            )
            .map((sc) => ({
              shareholdingId: sc.shareholdingId,
              sharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
              newSharesAllocated: sc.newSharesAllocated ?? 0,
            })),
          exitAmendmentDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          exitId,
          ...(isBeingCorrected && amendmentId
            ? { exitAmendmentId: amendmentId }
            : {}),
          // For original exit corrections, send the new exitDate. For amendment corrections, don't send it.
          ...(isBeingCorrected && !amendmentId
            ? { exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy') }
            : {}),
          ...(isBeingCorrected && exitDetails.selectedShareClasses !== undefined
            ? { companyIds: exitDetails.selectedShareClasses }
            : {}),
          ...(isBeingCorrected && exitDetails.selectedAllocations !== undefined
            ? { shareAllocationUploadIds: exitDetails.selectedAllocations }
            : {}),
          ...(isBeingCorrected && exitDetails.shareConsiderationCompanyId
            ? {
                shareConsiderationCompanyId:
                  exitDetails.shareConsiderationCompanyId,
              }
            : {}),
          ...(isBeingCorrected && exitDetails.totalConsiderationShares
            ? {
                totalConsiderationShares: Number(
                  exitDetails.totalConsiderationShares,
                ),
              }
            : {}),
          ...(isBeingCorrected &&
          exitDetails.shareConsiderationEisEligible !== undefined
            ? {
                shareConsiderationEisEligible:
                  exitDetails.shareConsiderationEisEligible,
              }
            : {}),
          isInitialConsideration: exitDetails.isInitialConsideration ?? true,
          shareConsiderationValuePerShare: Number(
            exitDetails.shareConsiderationValuePerShare ?? '0',
          ),
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId ?? '',
        });
      } else {
        // Use correct endpoint for corrections, edit for normal edits
        const mutationToUse = isBeingCorrected ? correctExit : edit;

        const shareholdingsToSubmit = shareholdings
          .filter(removeEmptyShareholdingRows)
          .map((sh) => {
            return {
              shareholdingId: sh.shareholdingId,
              sharesAllocatedForSale: sh.sharesAllocatedForSale,
              totalSaleValue: sh.totalSaleValue,
              taxableValue: sh.grossTaxableValue ?? sh.taxableValue,
              performanceFee: sh.performanceFee,
              sharePrice: sh.sharePrice,
              additionalGrossReceivedValue:
                sh.additionalGrossReceivedValue ?? 0,
              ...(exitDetails.chargeAccruedFees &&
              sh.accruedFeesCharged !== undefined
                ? { accruedFeesCharged: sh.accruedFeesCharged }
                : {}),
            };
          });

        mutationToUse.mutate({
          ...commonMutationInput,
          shareholdingsToBeExited: shareholdingsToSubmit,
          exitAmendmentDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          exitId,
          ...(isBeingCorrected && amendmentId
            ? { exitAmendmentId: amendmentId }
            : {}),
          // For original exit corrections, send the new exitDate. For amendment corrections, don't send it.
          ...(isBeingCorrected && !amendmentId
            ? { exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy') }
            : {}),
          ...(isBeingCorrected && exitDetails.selectedShareClasses !== undefined
            ? { companyIds: exitDetails.selectedShareClasses }
            : {}),
          ...(isBeingCorrected && exitDetails.selectedAllocations !== undefined
            ? { shareAllocationUploadIds: exitDetails.selectedAllocations }
            : {}),
        });
      }
    } else {
      // Check if this is a share consideration exit
      const isShareConsideration =
        exitDetails.considerationType === ConsiderationType.Share;

      if (isShareConsideration) {
        createShareConsiderationExit.mutate({
          ...commonMutationInput,
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          shareConsiderationsToBeExited: shareConsiderations
            .filter(
              (sc) =>
                (sc.sharesAllocatedForSale ?? 0) > 0 ||
                (sc.newSharesAllocated ?? 0) > 0,
            )
            .map((sc) => ({
              shareholdingId: sc.shareholdingId,
              sharesAllocatedForSale: sc.sharesAllocatedForSale ?? 0,
              newSharesAllocated: sc.newSharesAllocated ?? 0,
            })),
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses ?? [],
          shareConsiderationCompanyId:
            exitDetails.shareConsiderationCompanyId ?? '',
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId ?? '',
          shareConsiderationValuePerShare: Number(
            exitDetails.shareConsiderationValuePerShare ?? '0',
          ),
          totalConsiderationShares: Number(
            exitDetails.totalConsiderationShares ?? '0',
          ),
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible ?? false,
        });
      } else {
        create.mutate({
          ...commonMutationInput,
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          shareholdingsToBeExited: shareholdings
            .filter(removeEmptyShareholdingRows)
            .map((sh) => ({
              shareholdingId: sh.shareholdingId,
              sharesAllocatedForSale: sh.sharesAllocatedForSale,
              totalSaleValue: sh.totalSaleValue,
              taxableValue: sh.grossTaxableValue ?? sh.taxableValue,
              performanceFee: sh.performanceFee,
              sharePrice: sh.sharePrice,
              ...(exitDetails.chargeAccruedFees &&
              sh.accruedFeesCharged !== undefined
                ? { accruedFeesCharged: sh.accruedFeesCharged }
                : {}),
            })),
          exitDate: format(exitDetails.exitDate, 'dd/MM/yyyy'),
          companyIds: exitDetails.selectedShareClasses ?? [],
        });
      }
    }
  };

  const onCancel = () => {
    setExitDetailsStepCompleted(false);
    // Reset exitDetails in edit/correction mode - for new exits, keep the form values
    if (isBeingEdited || isBeingCorrected) {
      setExitDetails(previousExitDetails);
    }
    // Clear only table-related state
    setShareholdings([]);
    setShareConsiderations([]);
    setDisableTable(false);
    setIsUploadComplete(false);
    setSkipPrepopulation(false);
  };

  const pageTitle = isBeingCorrected
    ? 'Correct Exit'
    : isBeingEdited
    ? 'Exits: Update exit'
    : 'Exits: Process new exit';

  const pageContentHeader = isBeingCorrected
    ? 'Enter exit corrections'
    : 'Enter exit details';

  // Hide buttons when upload section is visible (before upload is complete)
  // Don't hide buttons when editing or correcting an amendment
  const shouldHideButtons =
    exitDetails?.considerationType === ConsiderationType.Share &&
    exitDetails?.shareConsiderationAllocationMethod ===
      ShareConsiderationAllocationMethod.Upload &&
    !isBeingEdited &&
    !isCorrectingAmendment &&
    shareConsiderations.length &&
    !isUploadComplete;

  // Debug log what's being passed to ExitDetailsForm
  const previousExitDetailsForForm =
    isBeingEdited || isBeingCorrected
      ? previousExitDetails
      : exitDetails
      ? {
          firmId: exitDetails.firmId,
          firmName: exitDetails.firmName,
          exitDate: exitDetails.exitDate,
          selectedCompany: exitDetails.selectedCompany,
          selectedShareClasses: exitDetails.selectedShareClasses,
          companyIds:
            exitDetails.companyIds ?? exitDetails.selectedShareClasses ?? [],
          selectedAllocations: exitDetails.selectedAllocations,
          sharesToBeSold: exitDetails.sharesToBeSold,
          sharePrice: exitDetails.sharePrice,
          taxableValueSharePrice: exitDetails.taxableValueSharePrice,
          taxableValueTotal: exitDetails.taxableValueTotal,
          receivedValueTotal: exitDetails.receivedValueTotal,
          isReceivedValueSharePriceEdited:
            exitDetails.isReceivedValueSharePriceEdited,
          chargeAccruedFees: exitDetails.chargeAccruedFees,
          taxReliefElegible: exitDetails.taxReliefElegible,
          taxReliefElegibleAllocationIds:
            exitDetails.taxReliefElegibleAllocationIds,
          shareAllocationUploadIds: exitDetails.selectedAllocations ?? [],
          considerationType: exitDetails.considerationType,
          shareConsiderationCompanyId: exitDetails.shareConsiderationCompanyId,
          shareConsiderationShareClassId:
            exitDetails.shareConsiderationShareClassId,
          shareConsiderationShareClassIds:
            exitDetails.shareConsiderationShareClassId
              ? [exitDetails.shareConsiderationShareClassId]
              : exitDetails.shareConsiderationShareClassIds,
          totalConsiderationShares: exitDetails.totalConsiderationShares,
          shareConsiderationValuePerShare:
            exitDetails.shareConsiderationValuePerShare,
          shareConsiderationValueTotal:
            exitDetails.shareConsiderationValueTotal,
          shareConsiderationEisEligible:
            exitDetails.shareConsiderationEisEligible,
          shareConsiderationAllocationMethod:
            exitDetails.shareConsiderationAllocationMethod,
          permitPartialShareAllocation:
            exitDetails.permitPartialShareAllocation,
        }
      : undefined;

  // Transform previousExitDetails to ensure shareConsiderationShareClassId is in the right format
  // Memoize to prevent unnecessary re-renders of child component
  const transformedPreviousExitDetails = useMemo(
    () =>
      previousExitDetailsForForm
        ? {
            ...previousExitDetailsForForm,
            shareConsiderationShareClassIds:
              previousExitDetailsForForm.shareConsiderationShareClassId
                ? [previousExitDetailsForForm.shareConsiderationShareClassId]
                : previousExitDetailsForForm.shareConsiderationShareClassIds,
          }
        : undefined,
    [previousExitDetailsForForm],
  );

  if (isSuccess) {
    return (
      <FullPageSuccess
        title="Your exit has been successfully processed"
        additionalText="Your investors will now be able to view this exit in their investor portals. Would you like to send an exit statement?"
        proceedText="Send exit statement"
        proceedLink={createExitStatementAddRoute(
          exitId ?? '',
          amendmentId ?? '',
        )}
        secondaryText="Do this later"
        secondaryLink={EXITS}
        bottomText={
          <BottomTextContent
            disableButton={isDownloading}
            handleDownload={handleDownload}
          />
        }
      />
    );
  }

  return (
    <PageContainer heading={pageTitle}>
      <PageContent header={pageContentHeader}>
        <RecordView size="full">
          <ExitDetailsForm
            isBeingEdited={isBeingEdited || !!isCorrectingAmendment}
            exitId={exitId}
            isBeingCorrected={isBeingCorrected}
            isCorrectingAmendment={!!isCorrectingAmendment}
            previousExitDetails={transformedPreviousExitDetails}
            onSubmit={(details) => {
              const formValues: ExitDetailsFormValues = {
                firmId: details.firmId ?? '',
                firmName: details.firmName ?? '',
                exitDate: details.exitDate,
                selectedCompany: {
                  _id: details.selectedCompany?._id ?? '',
                  tradingName: details.selectedCompany?.tradingName ?? '',
                },
                sharesToBeSold: details.sharesToBeSold ?? 0,
                sharePrice: details.sharePrice.toString(),
                taxableValueSharePrice:
                  details.taxableValueSharePrice.toString(),
                taxableValueTotal: details.taxableValueTotal ?? '0',
                receivedValueTotal: details.receivedValueTotal ?? '0',
                isReceivedValueSharePriceEdited: false,
                chargeAccruedFees: details.chargeAccruedFees,
                taxReliefElegible: details.taxReliefElegible,
                selectedShareClasses: details.selectedShareClasses ?? [],
                companyIds: details.selectedShareClasses ?? [],
                selectedAllocations: details.selectedAllocations ?? [],
                shareAllocationUploadIds: details.selectedAllocations ?? [],
                taxReliefElegibleAllocationIds:
                  details.taxReliefElegibleAllocationIds ?? [],
                considerationType: details.considerationType,
                shareConsiderationCompanyId:
                  details.shareConsiderationCompanyId,
                shareConsiderationShareClassId:
                  details.shareConsiderationShareClassId,
                totalConsiderationShares:
                  details.totalConsiderationShares?.toString() ?? '0',
                shareConsiderationValuePerShare:
                  details.shareConsiderationValuePerShare?.toString() ?? '0',
                shareConsiderationValueTotal:
                  details.shareConsiderationValueTotal?.toString() ?? '0',
                shareConsiderationEisEligible:
                  details.shareConsiderationEisEligible,
                shareConsiderationAllocationMethod:
                  details.shareConsiderationAllocationMethod,
                isInitialConsideration: details.isInitialConsideration,
                permitPartialShareAllocation:
                  details.permitPartialShareAllocation ?? false,
              };

              // If correcting, show the correction behavior modal first
              if (isBeingCorrected) {
                setPendingExitDetails(formValues);
                setMaintainOriginalAllocations(true); // Reset to default
                correctionBehaviorDisclosure.onOpen();
              } else {
                // For edits and new exits, proceed directly
                setExitDetails(formValues);
                setExitDetailsStepCompleted(true);
                setIsUploadComplete(false);

                // If form was modified during correction, clear existing shareholdings
                // so they get recalculated with new values
                if (details.isFormModified) {
                  setSkipPrepopulation(true);
                  setShareholdings([]);
                  setShareConsiderations([]);
                } else {
                  setSkipPrepopulation(false);
                }
              }
            }}
            exitDetailsStepCompleted={exitDetailsStepCompleted}
          />
        </RecordView>

        {considerationHistoryEnabled &&
          exitId &&
          exit.data?.firmId &&
          considerationHistory.data &&
          considerationHistory.data.length > 0 && (
            <>
              <ConsiderationHistory
                exitId={exitId}
                firmId={exit.data.firmId}
                isBeingCorrected={isBeingCorrected}
              />
            </>
          )}

        {exitDetailsStepCompleted && (
          <>
            <StyledHeading variant="h2">
              Review and update exit results
            </StyledHeading>

            {exitDetails &&
            exitDetails.considerationType === ConsiderationType.Share ? (
              <ReviewShareConsiderations
                exitDetails={exitDetails}
                shareConsiderations={shareConsiderations}
                setShareConsiderations={setShareConsiderations}
                disableTable={disableTable ?? false}
                isBeingEdited={isBeingEdited || !!isCorrectingAmendment}
                exitId={exitId}
                onUploadComplete={() => setIsUploadComplete(true)}
                isBeingCorrected={isBeingCorrected}
                maintainOriginalExitDetails={
                  isBeingCorrected ? maintainOriginalAllocations : undefined
                }
                previousExitDetails={previousExitDetails}
              />
            ) : exitDetails ? (
              <ReviewShareholdings
                exitDetails={exitDetails}
                previousExitDetails={previousExitDetails}
                shareholdings={shareholdings}
                setShareholdings={setShareholdings}
                disableTable={disableTable}
                isBeingEdited={isBeingEdited || !!isCorrectingAmendment}
                isBeingCorrected={!!isBeingCorrected}
                exitId={exitId}
                exitAmendmentId={amendmentId ?? ''}
                maintainOriginalExitDetails={
                  isBeingCorrected ? maintainOriginalAllocations : undefined
                }
              />
            ) : null}
            {shouldHideButtons ? null : (
              <ButtonsAndErrorModals
                exitDetails={exitDetails}
                shareholdings={shareholdings}
                setShareholdings={setShareholdings}
                shareConsiderations={shareConsiderations}
                setShareConsiderations={setShareConsiderations}
                onSubmit={onSubmit}
                onCancel={onCancel}
                isLoading={
                  edit.isPending ||
                  create.isPending ||
                  createShareConsiderationExit.isPending ||
                  editShareConsiderationExit.isPending ||
                  correctExit.isPending ||
                  correctShareConsiderationExit.isPending
                }
                disableTable={disableTable}
                isBeingEdited={isBeingEdited || !!isCorrectingAmendment}
                isBeingCorrected={!!isBeingCorrected}
                previousExitDetails={previousExitDetails}
              />
            )}
          </>
        )}

        {/* Correction Behavior Modal */}
        <AlertDialog
          title="Correction behaviour"
          content={
            <CorrectionModalContent>
              <p>
                Please select how your corrections will affect the exit's
                existing fee and share allocations.
              </p>
              <CorrectionBehaviorContainer>
                <label htmlFor="correctionBehavior">Behaviour:</label>
                <CorrectionBehaviorSelectWrapper>
                  <BasicSelect
                    id="correctionBehavior"
                    value={maintainOriginalAllocations ? 'maintain' : 'run-new'}
                    onChange={(value) =>
                      setMaintainOriginalAllocations(value === 'maintain')
                    }
                    options={[
                      {
                        value: 'maintain',
                        label: 'Maintain original allocations',
                      },
                      {
                        value: 'run-new',
                        label: 'Run new allocations',
                      },
                    ]}
                  />
                </CorrectionBehaviorSelectWrapper>
              </CorrectionBehaviorContainer>
              {maintainOriginalAllocations && (
                <CorrectionBehaviorDescription>
                  Maintaining original allocations will require manual editing
                  in the review table if shares or fees require updating.
                  Changes to the exit's received or taxable value will always be
                  reflected in a corrected allocation.
                </CorrectionBehaviorDescription>
              )}
            </CorrectionModalContent>
          }
          open={correctionBehaviorDisclosure.isOpen}
          onClose={correctionBehaviorDisclosure.onClose}
          onConfirm={() => {
            correctionBehaviorDisclosure.onClose();
            // Now set the exit details and proceed
            if (pendingExitDetails) {
              setExitDetails(pendingExitDetails);
              setExitDetailsStepCompleted(true);
              setIsUploadComplete(false);
              setSkipPrepopulation(true);
              setShareholdings([]);
              setShareConsiderations([]);
            }
          }}
          btnLabels={{ confirm: 'Submit', cancel: 'Cancel' }}
          cancelBtnProps={{ color: 'primary' }}
        />
      </PageContent>
    </PageContainer>
  );
};

export default CreateExit;
